import {
  Injectable,
  Signal,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";
import { Subscription } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import {
  Auth,
  authState,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
  updateEmail,
  GoogleAuthProvider,
  FacebookAuthProvider,
  User,
} from "@angular/fire/auth";
import { RoutingService } from "./routing.service";
import { TrackingService } from "./tracking.service";
import { TranslateService } from "@ngx-translate/core";
import { BvDialogService } from "../design-system";
import { ConfigService } from "./config.service";

@Injectable()
export class AuthService {
  user: Observable<User | null>;
  // Reactive uid root — null when signed out. The per-domain signal stores
  // (Profile/Club/…) key off this so they re-fetch on login and reset on
  // logout. `get uid()` below keeps the synchronous field-style read sites
  // (~44 of them) working unchanged.
  readonly uidSignal: Signal<string | null>;
  // The full current Firebase user (or null). Consumed by ProfileService's
  // one-time login/signup bootstrap.
  readonly authUser: Signal<User | null>;
  subscribers: Subscription[] = [];
  userInfo: any;
  altRoute!: string;
  engageParams: any;
  constructor(
    private fbAuth: Auth,
    private injector: EnvironmentInjector,
    private tracking: TrackingService,
    private translate: TranslateService,
    public dialog: BvDialogService,
    private routing: RoutingService,
    private configServ: ConfigService
  ) {
    // Establish ONE Firebase auth listener here (in the constructor's injection
    // context) and shareReplay it. The modular `authState()` is zone-wrapped and
    // registers an onAuthStateChanged listener on EVERY subscribe — so external
    // subscribers (connection-router, login/signup/lobby/tournament in ngOnInit)
    // would each create a listener OUTSIDE an injection context, which logs
    // AngularFire's zone-instability warning and destabilizes change detection
    // (seen as NG0100/NG0956 churn on logout). With shareReplay (no refCount) the
    // source is subscribed once and late subscribers just replay the last value.
    this.user = authState(this.fbAuth).pipe(
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.uidSignal = toSignal(
      this.user.pipe(map((u) => (u ? u.uid : null))),
      { initialValue: null }
    );
    this.authUser = toSignal(this.user, { initialValue: null });
    this.user.subscribe((userData) => {
      if (userData) {
        this.userInfo = {
          uid: userData.uid,
          userName: userData.displayName,
          email: userData.email,
          imageUrl: userData.providerData[0].photoURL || "",
        };
        tracking.identify(userData.uid);
        dialog.closeAll();
      }
    });
  }

  /** Synchronous current uid (null when signed out). Backed by `uidSignal`. */
  get uid(): string | null {
    return this.uidSignal();
  }

  // AngularFire's modular auth functions call inject(NgZone) internally, so every
  // method-level call must run inside an injection context (same pattern as the
  // modular Database migration). The constructor is already an injection context,
  // so authState()/toSignal() above need no wrapping.
  private run<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  socialLogin(method: string) {
    let provider: GoogleAuthProvider | FacebookAuthProvider;
    if (method == "google") {
      provider = new GoogleAuthProvider();
      provider.addScope("email");
    } else if (method == "facebook") {
      provider = new FacebookAuthProvider();
      provider.addScope("email");
    } else return;
    // Mobile browsers with partitioned storage (Chrome Android, Safari iOS)
    // break signInWithRedirect with "missing initial state" — the
    // *.firebaseapp.com iframe can't read the session-storage it wrote
    // pre-redirect. Use popup on mobile, redirect on desktop.
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const flow = this.run(() =>
      isMobile
        ? signInWithPopup(this.fbAuth, provider)
        : signInWithRedirect(this.fbAuth, provider)
    );
    flow.catch((e: any) => console.log(e.code));
  }

  logout() {
    let sub: any;
    while (this.subscribers && this.subscribers.length) {
      sub = this.subscribers.pop();
      if (sub) sub.unsubscribe();
    }
    setTimeout(() => {
      this.run(() => signOut(this.fbAuth));
    });
  }
  addSub(sub: Subscription) {
    this.subscribers.push(sub);
  }
  loginEmail(email: string, password: string) {
    let th = this;
    this.run(() =>
      signInWithEmailAndPassword(this.fbAuth, email || "", password || "")
    ).catch(function (error: any) {
      th.translate.get(th.stripCode(error.code)).subscribe((res: string) => {
        alert(res);
      });
    });
  }
  createEmail(email: string, password: string) {
    let th = this;
    this.run(() =>
      createUserWithEmailAndPassword(this.fbAuth, email || "", password || "")
    ).catch(function (error: any) {
      th.translate.get(th.stripCode(error.code)).subscribe((res: string) => {
        alert(res);
      });
    });
  }
  resetPassword(email: string) {
    let th = this;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      th.translate.get("ENTER_VALID_EMAIL").subscribe((res: string) => {
        alert(res);
      });
      return;
    }
    this.run(() => sendPasswordResetEmail(this.fbAuth, email.trim())).then(
      function () {
        th.translate.get("PASSWORD_RESET_SENT").subscribe((res: string) => {
          alert(res);
        });
      },
      function (error: any) {
        th.translate.get(th.stripCode(error.code)).subscribe((res: string) => {
          alert(res);
        });
      }
    );
  }
  updateEmailAddress(email: string, password: any) {
    return new Promise((resolve, reject) => {
      this.run(() =>
        signInWithEmailAndPassword(this.fbAuth, this.userInfo.email, password)
      )
        .then((userCredential) => {
          this.run(() => fetchSignInMethodsForEmail(this.fbAuth, email))
            .then((res) => {
              if (res.includes("password"))
                resolve("auth/email-already-in-use");
              else {
                this.run(() => updateEmail(userCredential.user, email))
                  .then(() => {
                    resolve("Email updated");
                  })
                  .catch((error) => {
                    resolve(error);
                  });
              }
            })
            .catch((error) => {
              console.log("Error", error);
              resolve(error);
            });
        })
        .catch((error) => {
          resolve(error);
        });
    });
  }

  // Send a verification email to the currently signed-in user. Centralized here
  // so AuthService stays the single owner of the Firebase Auth instance.
  sendEmailVerification() {
    const current = this.fbAuth.currentUser;
    if (!current) return Promise.resolve();
    return this.run(() => sendEmailVerification(current));
  }

  stripCode(errorCode: string) {
    if (errorCode && errorCode.length && errorCode.split)
      return errorCode.split("/")[1];
    else return errorCode;
  }
  getProvider(userData: any) {
    let tmpProvider = userData.providerData[0].providerId;
    if (tmpProvider == "google.com") tmpProvider = "google";
    else if (tmpProvider == "facebook.com") tmpProvider = "facebook";
    return tmpProvider;
  }
  /**
   * get user connected
   */
  getUserConnected() {
    return this.userInfo;
  }
}
